import {
  SyncProblemOutlined as ResetIcon,
  AutoModeOutlined as RestartIcon,
  WarningAmberRounded,
} from "@mui/icons-material";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DialogPrompt,
  DialogPromptModalType,
} from "../../components/DialogPrompt/DialogPrompt";
import { useNotifications } from "../../hooks/useNotifications";
import {
  sendAnonymousResetRequest,
  sendAnonymousRestartRequest,
  tryParseURL,
} from "../../utils/systemUtils";
import { LOGIN_PAGE_NS } from "./i18n";

// We don't know the origin of the web front-end but we can retrieve it from
// the redirect_uri query param in the encoded ReturnUrl of the current address
// This is needed to interact with the electron app and restart/reset the system
const getRedirectWebHostOrigin = (loginPageUrl: string): string => {
  const url = tryParseURL(loginPageUrl) ?? new URL(window.location.href);
  const fallbackOrigin = `${url.protocol}//${url.hostname}`;

  const returnUrl = tryParseURL(url.searchParams.get("ReturnUrl"));
  if (!returnUrl) {
    return fallbackOrigin;
  }

  const redirectUri = tryParseURL(returnUrl.searchParams.get("redirect_uri"));
  if (!redirectUri) {
    return fallbackOrigin;
  }

  return redirectUri.origin;
};

export const SyncError = () => {
  const webHostOrigin = getRedirectWebHostOrigin(window.location.href);
  const notifications = useNotifications();
  const { t } = useTranslation(LOGIN_PAGE_NS);
  const {
    palette: { warning, text },
  } = useTheme();

  const [modalType, setModalType] = useState(DialogPromptModalType.None);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const closeModal = () => setIsOpenModal(false);
  const openModal = (modalType: DialogPromptModalType) => {
    setModalType(modalType);
    setIsOpenModal(true);
  };

  const tryResetSystem = useCallback(
    () =>
      (async () => {
        const willReset = await sendAnonymousResetRequest(webHostOrigin);
        if (!willReset) {
          notifications.error({
            message: t("syncing.notification.message"),
            description: t("syncing.notification.reset"),
          });
        }
      })(),
    [notifications, webHostOrigin, t]
  );

  const tryRestartSystem = useCallback(
    () =>
      (async () => {
        const willRestart = await sendAnonymousRestartRequest(webHostOrigin);
        if (!willRestart) {
          notifications.error({
            message: t("syncing.notification.message"),
            description: t("syncing.notification.restart"),
          });
        }
      })(),
    [notifications, webHostOrigin, t]
  );

  const modalActions = useMemo(
    () => ({
      [DialogPromptModalType.Reset]: tryResetSystem,
      [DialogPromptModalType.Restart]: tryRestartSystem,
      [DialogPromptModalType.None]: () => {},
    }),
    [tryResetSystem, tryRestartSystem]
  );

  const handleModalAction = useCallback(() => {
    modalActions[modalType]();
    closeModal();
  }, [modalActions, modalType]);

  const dialogPrompt = useMemo(
    () => (
      <DialogPrompt
        isOpenModal={isOpenModal}
        handleCloseModal={closeModal}
        modalType={modalType}
        handleActionModal={handleModalAction}
      />
    ),
    [handleModalAction, isOpenModal, modalType]
  );

  return (
    <Box>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <WarningAmberRounded sx={{ color: warning.main, fontSize: "6em" }} />
        <Typography mt={2} align={"center"}>
          {t("syncing.message")}
        </Typography>
      </Box>
      <Box mt={2}>
        <Button
          onClick={() => openModal(DialogPromptModalType.Restart)}
          startIcon={<RestartIcon />}
          size={"large"}
          sx={{ color: text.primary }}
        >
          {t("syncing.button.restart")}
        </Button>
        <Button
          onClick={() => openModal(DialogPromptModalType.Reset)}
          startIcon={<ResetIcon />}
          size={"large"}
          sx={{ color: text.primary }}
        >
          {t("syncing.button.reset")}
        </Button>
        {dialogPrompt}
      </Box>
    </Box>
  );
};
